/**
 * Dashboard v1.5 — shared vocabulary.
 *
 * The theme union is NOT redeclared here. A second `'dark' | 'light'` union is a second design
 * system, so the dashboard re-exports Header's and reads the same `HeaderTheme.module.css`; light
 * stays one token swap for the whole of v1.5 rather than one per folder.
 *
 * Artboards: `D1 Dashboard`, `D2 Dashboard · generating`, `D3 Dashboard · nothing yet`,
 * `D4 Dashboard · hover`, `D5 Dashboard · deck actions`, `D6 Dashboard · time of day`.
 */

import { cn } from '~/lib/utils';
import headerThemes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import dashboardThemes from './DashboardTheme.module.css';

export type { HeaderHover, HeaderTheme } from '~/design-system/components/v1_5/Header/theme';
import type { HeaderTheme } from '~/design-system/components/v1_5/Header/theme';

/**
 * Applies BOTH theme layers, exactly as `Chat/theme.ts`'s `chatThemeClass` does.
 *
 * The dashboard reuses the header's `--hv15-*` ramp wholesale — text tones, fills, borders, motion
 * curves, durations — rather than restating it under a second name. `--dv15-*` adds only the six
 * things the header never had to name; see `DashboardTheme.module.css`.
 *
 * Every dashboard component's root does
 * `className={cn(dashboardThemeClass(theme), styles.root, className)}`. Custom properties inherit,
 * so a themed composite themes every atom inside it for free.
 */
export function dashboardThemeClass(theme: HeaderTheme = 'dark'): string {
  return cn(headerThemes[theme], dashboardThemes[theme]);
}

/**
 * The four greetings `D6 Dashboard · time of day` draws, named for the hour rather than for the
 * words — "Still up" is not an evening, it is the fourth thing the clock can say.
 */
export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'late';

/**
 * The greeting line, verbatim from D6's four children. `{name}` is the only variable part; the
 * artboards draw "Guri" in all four.
 *
 * Kept as a map rather than as a template picked in the component, because the fourth string breaks
 * the pattern the first three set — "Still up, Guri" is not "Good <something>, Guri" — and any
 * component that built the sentence would have to special-case it.
 */
export const GREETING: Record<TimeOfDay, (name: string) => string> = {
  morning: (name) => `Good morning, ${name}`,
  afternoon: (name) => `Good afternoon, ${name}`,
  evening: (name) => `Good evening, ${name}`,
  late: (name) => `Still up, ${name}`,
};

/**
 * Which greeting a given local time gets.
 *
 * **The boundaries are NOT in the design.** D6 draws the four strings and says nothing about when
 * each one is due, so these four are the conventional English ones (05 morning, 12 afternoon,
 * 17 evening, 22 late) written down here so there is exactly one answer rather than one per caller.
 * Move them freely — nothing else in this folder reads the clock.
 */
export function timeOfDayFor(date: Date = new Date()): TimeOfDay {
  const hour = date.getHours();
  if (hour < 5) return 'late';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'late';
}

/**
 * Where a deck is in its life.
 *
 * `ready` is the overwhelming majority and is the absence of the other one. `generating` is D2: the
 * cover has no render yet, a badge sits on it, and the meta line says what is being written instead
 * of when the deck was last touched — `writing 3 of 16`, never `16 artifacts · 3m ago`. A relative
 * time on something that does not exist yet is a lie. The card does not enforce that; the caller
 * writes the string.
 */
export type DeckStatus = 'ready' | 'generating';

/**
 * Rows of a deck card's `⋯` menu, in the order `D5 Dashboard · deck actions` draws them.
 *
 * Three, and the route's own handlers are exactly these three
 * (`onClick` / `onRename` / `onDelete` in `app/routes/dashboard.tsx`), so the menu is drawn wired
 * or not at all.
 */
export type DeckAction = 'rename' | 'delete';

/** One card in the grid. */
export interface DeckSummary {
  id: string;
  /** Line one, 14/19. The deck's name. */
  title: string;
  /**
   * Line two, 12/17, verbatim — the card never assembles this string. The separator the design uses
   * throughout is U+00B7 with a space either side: `16 artifacts · 3m ago`. While `generating` it is
   * the progress instead: `writing 3 of 16`.
   */
  meta: string;
  /**
   * The deck's own render. A CSS `image` value — a `url(...)`, a gradient, anything
   * `background-image` takes. Absent means "no render yet", which is drawn as the two-band
   * placeholder, and is what a generating deck always looks like.
   */
  cover?: string;
  /** Defaults to `ready`. */
  status?: DeckStatus;
}

/**
 * The nine cover colours the artboards draw, in the order they first appear across D1's twelve
 * cards. They are placeholder art standing in for a deck's first slide, not semantic colour — the
 * only reason they are named at all is that the stories need to reproduce the boards exactly, and
 * twelve pairs of raw hexes scattered through a story file is how a palette stops being one.
 */
export const DECK_COVER_TONES = {
  cyan: '#22B8E8',
  indigo: '#635BFF',
  steel: '#7C93A8',
  slate: '#3F4A54',
  olive: '#8B8A63',
  moss: '#4B5A47',
  blush: '#C9A89A',
  plum: '#5C4A6E',
  clay: '#6E5C4A',
} as const;

export type DeckCoverTone = keyof typeof DECK_COVER_TONES;

/**
 * A two-band cover, as a CSS `image` value to hand to `DeckCard`'s `cover`.
 *
 * 65.93% is the drawn split: the top band is 89 of the cover's 135 inner pixels. Expressed as a
 * percentage rather than as `89px` so the bands keep their proportion when the grid gives a card
 * more width than the 244 the boards are drawn at — a fixed 89px band on a 260px-wide card is a
 * different picture.
 */
export function coverBands(top: DeckCoverTone, bottom: DeckCoverTone): string {
  const a = DECK_COVER_TONES[top];
  const b = DECK_COVER_TONES[bottom];
  return `linear-gradient(${a} 0 65.93%, ${b} 65.93% 100%)`;
}
